import { useRef } from "react";
import { Menu, Divider } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { useTranslation } from "@/lib/i18n";
import { MenuItem } from "@/features/admin/components";
import { useUpdateDashboardWarningMutation } from "@/features/admin/features/dashboards";
import { ModalComponent, confirm, showToast } from "@/features/shared/components";
import { UpdateLegacyWarningSettingBodyLegacyWarningSettings as UpdatePayload } from "@/lib/api";
import { useAdminDashboard } from "@/features/admin/hooks";

import { LegacyDashboardWarningSettingKey } from "./legacy-warning-setting-keys";

import WarningsSettingsDialog from ".";

type Props = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  warning: LegacyDashboardWarningSettingKey | null;
  allWarnings?: UpdatePayload;
};

const EditWarningsMenu = ({ anchorEl, onClose, warning, allWarnings }: Props) => {
  const { t } = useTranslation();
  const updateWarnings = useUpdateDashboardWarningMutation();

  const dashboard = useAdminDashboard();

  const open = Boolean(anchorEl);

  const editWarningModalRef = useRef<ModalComponent>();

  const handleDelete = async () => {
    const confirmed = await confirm(
      t("admin.dashboard_settings.warnings_settings.remove_confirm.title"),
      t("admin.dashboard_settings.warnings_settings.remove_confirm.body"),
    );

    if (confirmed && warning) {
      updateWarnings.mutate(
        {
          dashboardId: dashboard.id,
          body: {
            legacy_warning_settings: {
              ...allWarnings,
              [warning as keyof UpdatePayload]: undefined,
            },
          },
        },
        {
          onSuccess: () => {
            onClose();
            showToast(t("admin.messages.warnings_deleted"));
          },
        },
      );
    }
  };

  return (
    <>
      <Menu anchorEl={anchorEl} open={open} onClose={onClose}>
        <MenuItem
          icon={<EditIcon fontSize="small" />}
          title={t("admin.dashboard_settings.warnings_settings.menu.options.edit")}
          onClick={() => editWarningModalRef.current?.open?.()}
        />
        <Divider />
        <MenuItem
          title={t("admin.dashboard_settings.warnings_settings.menu.options.delete")}
          icon={<DeleteIcon fontSize="small" />}
          onClick={handleDelete}
        />
      </Menu>
      <WarningsSettingsDialog
        ref={editWarningModalRef}
        initialValues={warning ? { [warning]: allWarnings?.[warning] } : undefined}
        onClose={onClose}
      />
    </>
  );
};

export default EditWarningsMenu;
